/* Randomize array in-place using Durstenfeld shuffle algorithm */
export function shuffleArray(array) {
  for (var i = array.length - 1; i > 0; i--) {
    var j = Math.floor(Math.random() * (i + 1))
    var temp = array[i]
    array[i] = array[j]
    array[j] = temp
  }
}
export function shuffle(array) {
  const newArray = [...array]
  shuffleArray(newArray)
  return Object.freeze(newArray)
}

// using lists for ordered sets
export function setAdd(list1, component) {
  if (!list1.includes(component)) {
    return Object.freeze(list1.concat(component))
  }
  return Object.freeze(list1)
}

export function setRemove(list1, component) {
  return Object.freeze(list1.filter((i) => i !== component))
}

export function setMerge(list1, list2) {
  list2.forEach((elem) => setAdd(list1, elem))
  return list1
}

/*
 * @doctests
 *
 * ```js
 * t.deepEqual(range(0, 4, 1), [ 0, 1, 2, 3 ])
 * t.deepEqual(range(0, 4), [ 0, 1, 2, 3 ])
 * t.deepEqual(range(0, 4, 2), [ 0, 2 ])
 * t.deepEqual(range(0, 5, 2), [ 0, 2, 4 ])
 * t.deepEqual(range(10, 5, -1), [ 10, 9, 8, 7, 6 ])
 * t.deepEqual(range(1, 1), [])
 * t.deepEqual(range(1, 0), [])
 * t.deepEqual(range(1, -Infinity), [])
 * ```
 */
export function range(start, stop, step = 1) {
  if (start === stop) return []
  if (start < stop && step < 0) return []
  if (start > stop && step > 0) return []
  return Array(Math.ceil((stop - start) / step))
    .fill(start)
    .map((x, y) => x + y * step)
}

////////////////////////////////////////////////////////////////////////////////
export function removeItem(list, item) {
  return list.filter((i) => i.id !== item.id)
}

export function upsertItem(list, item) {
  return removeItem(list, item).concat(item)
}

/*
 * naive implementation don't use for large lists
 *
 * @doctests
 *
 * ```js
 * t.deepEqual(dedupe([]), [])
 * t.deepEqual(dedupe([1,2]), [1,2])
 * t.deepEqual(dedupe([1,2,2]), [1,2])
 * t.deepEqual(dedupe([2,1,2,4,1]), [2,1,4])
 * ```
 */
export const dedupe = (xs) => {
  if (xs.length <= 1) return xs
  return xs.slice(0, 1).concat(dedupe(xs.slice(1).filter((x) => x !== xs[0])))
}

/*
 * @doctests
 *
 * ```js
 * t.deepEqual(toDictOn([{id: 'a'}, {id: 'b'}, {id: 'c'}], 'id'), {a: {id: 'a'}, b: {id: 'b'}, c: {id: 'c'}})
 * ```
 */
export const toDictOn = (arr, key) =>
  arr.reduce((acc, obj) => {
    acc[obj[key]] = obj
    return acc
  }, {})

/*
 * Behavior:
 *    Same as dropWhile but works right to left
 *
 * @doctests
 *
 * ```js
 * t.deepEqual(dropWhileRight([1,2,3,4,5,6,7,8,9], x => x > 5), [1, 2, 3, 4, 5])
 * t.deepEqual(dropWhileRight([0,0,1,0,0], x => x === 0), [0,0, 1])
 * t.deepEqual(dropWhileRight(['a', '_', 'b', '_', '_', '_'], x => x === '_'), ['a', '_', 'b'])
 * ```
 */
export function dropWhileRight(iter, predicate) {
  const [, result] = iter.reduceRight(
    ([cont, acc], val) => {
      if (cont) {
        acc.unshift(val)
        return [true, acc]
      }
      if (predicate(val)) {
        return [false, acc]
      }
      acc.unshift(val)
      return [true, acc]
    },
    [false, []]
  )
  return result
}

/*
 * @doctests
 *
 * ```js
 * t.deepEqual(
 *  groupOn([{'a': 1, 'b':1}, {'a': 1, 'b':2}, {'a': 1,'b':3}], 'a'),
 *  [ [ { a: 1, b: 1 }, { a: 1, b: 2 }, { a: 1, b: 3 } ] ]
 * )
 * ```
 */
export function groupOn(iter, key) {
  return groupBy(iter, (a, b) => a[key] === b[key])
}

/*
 * Behavior:
 *    removes items from start of list until predicate is false
 *    then returns rest of list
 *
 * @doctests
 *
 * ```js
 * t.deepEqual(dropWhile(x => x < 5, [1,2,3,4,5,6,7,8,9]), [5,6,7,8,9])
 * t.deepEqual(dropWhile(x => x === 0, [0,0,1,0,0]), [1,0,0])
 * ```
 */
export function dropWhile(predicate, iter) {
  const [, result] = iter.reduce(
    ([cont, acc], val) => {
      if (cont) {
        acc.push(val)
        return [true, acc]
      }
      if (predicate(val)) {
        return [false, acc]
      }
      acc.push(val)
      return [true, acc]
    },
    [false, []]
  )
  return result
}

/*
 * @doctests
 *
 * ```js
 * t.deepEqual(groupBy([1,1,3,3,3,1]), [ [ 1, 1 ], [ 3, 3, 3 ], [ 1 ] ])
 * t.deepEqual(
 *  groupBy([{'a': 1, 'b':1}, {'a': 1, 'b':2}, {'a': 1,'b':3}], (a,b) => a.a === b.a),
 *  [ [ { a: 1, b: 1 }, { a: 1, b: 2 }, { a: 1, b: 3 } ] ]
 * )
 * ```
 * note: use lodash groupBy if you want global grouping
 */
export function groupBy(iter, eq = (a, b) => a === b) {
  return iter.reduceRight((grouped, val) => {
    if (!grouped.length) {
      return [[val]]
    }
    if (eq(grouped[0][0], val)) {
      grouped[0].unshift(val)
    } else {
      grouped.unshift([val])
    }
    return grouped
  }, [])
}

/*
 * @doctests
 *
 * partition with truthy result on left array
 *
 * ```js
 * t.deepEqual(partition([1,9,2,8,3,7], (x) => x < 4), [[1,2,3], [9,8,7]])
 * ```
 */
export function partition(arr, fn) {
  const part = [[], []]
  arr.forEach((x) => {
    part[+!fn(x)].push(x)
  })
  return part
}

/*
 * @doctests
 *
 * ```js
 * t.deepEqual(toArray(4), [4])
 * t.deepEqual(toArray([3,2]), [3,2])
 * ```
 */
export function toArray(arr) {
  return Array.isArray(arr) ? arr : [arr]
}
